
import { computed, defineComponent, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { cloneDeep, debounce, isEqual } from 'lodash';
import { useStore } from '@/store';
import { InputElementTypes, VolumeAndWeightUnits } from '@/enums';
import {
  createWritableComputedStoreMethodsFromStoreObject,
  getTimestampNow,
  toFixed,
  useModalState,
  useSharedInternalState,
  useSelectSubmittedClient,
  createEnumerableEnum,
  exportToDrive,
  createGeoPoint
} from '@/utils';
import { emptyValues, formDefaults } from '@/constants';
import { Bid, Client, MaterialListItem } from '@/types/app';
import MaterialIconButton from '@/components/form-controls/MaterialIconButton.vue';
import TextInput from '@/components/form-controls/base/TextInput.vue';
import EmailInput from '@/components/form-controls/base/EmailInput.vue';
import DateInput from '@/components/form-controls/base/DateInput.vue';
import ButtonInput from '@/components/form-controls/base/ButtonInput.vue';
import ClientsSelect from '@/components/form-controls/ClientsSelect.vue';
import CheckboxInput from '@/components/form-controls/base/CheckboxInput.vue';
import TextAreaInput from '@/components/form-controls/base/TextAreaInput.vue';
import NumberWithMetricInput from '@/components/form-controls/NumberWithMetricInput.vue';
import AppTable from '@/components/tables/base/AppTable.vue';
import ClientAddEditFormModal from '@/components/modal/ClientAddEditFormModal.vue';
import { Location } from '@/types/shared';
import 'mosha-vue-toastify/dist/style.css';
import { createToast } from 'mosha-vue-toastify';
import MaterialListItemDeleteFormModal from '../modal/MaterialListItemDeleteFormModal.vue';

export default defineComponent({
  name: 'Bid',
  components: {
    MaterialIconButton,
    TextInput,
    EmailInput,
    DateInput,
    ButtonInput,
    ClientsSelect,
    CheckboxInput,
    AppTable,
    ClientAddEditFormModal,
    NumberWithMetricInput,
    TextAreaInput,
    MaterialListItemDeleteFormModal
  },
  setup() {
    const store = useStore();
    const localBid = computed<Bid>(() => store.getters.bids.getLocalBid ?? formDefaults.localBid);
    const appDefaults = computed(() => store.getters.appDefaults.get[0] ?? formDefaults.appDefaults);
    const client = useSharedInternalState('linkedClientSelect');
    const jobName = useSharedInternalState('jobName');
    const enumerableVolumeAndWeightUnits = createEnumerableEnum(VolumeAndWeightUnits);

    const [selectedDeleteMaterialListItem, isDeleteModalOpen, openDeleteModal] = useModalState<MaterialListItem>();

    const bidWriteableComputedStoreMethods = createWritableComputedStoreMethodsFromStoreObject(
      localBid.value,
      store.dispatch.bids.updateLocalBid,
      ['jobLocation', 'completionDate', 'notes']
    );

    const defaultLocation: Location = reactive({
      geoPoint: createGeoPoint(0, 0),
      address: ''
    });

    const localBidMaterialListLocalState = computed(() =>
      localBid.value.materialList.map((materialListItem, index) => {
        const itemsWithLocation = {
          ...materialListItem,
          location: materialListItem.location ?? defaultLocation
        };

        const writableComputedMethods = createWritableComputedStoreMethodsFromStoreObject(
          itemsWithLocation,
          newValue => {
            const updatedMaterialList = localBid.value.materialList.slice();
            updatedMaterialList.splice(index, 1, {
              ...itemsWithLocation,
              ...newValue
            } as MaterialListItem);
            store.dispatch.bids.updateLocalBid({ materialList: updatedMaterialList });
          },
          ['materialDisplayName', 'vendorDisplayName', 'truckDisplayName']
        );

        const quantity = ref(cloneDeep(itemsWithLocation.quantity));
        const pricePerUnit = ref(cloneDeep(itemsWithLocation.pricePerUnit));

        watch(
          [quantity, pricePerUnit],
          ([newQuantity, newPricePerUnit]) => {
            quantity.value = newQuantity;
            pricePerUnit.value = newPricePerUnit;

            const updatedMaterialList = localBid.value.materialList.slice();
            updatedMaterialList.splice(index, 1, {
              ...itemsWithLocation,
              quantity: newQuantity,
              pricePerUnit: newPricePerUnit
            } as MaterialListItem);
            store.dispatch.bids.updateLocalBid({ materialList: updatedMaterialList });
          },
          { deep: true }
        );

        return {
          ...writableComputedMethods,
          quantity,
          pricePerUnit
        };
      })
    );

    const label = ref('Save');
    const revertLabel = debounce(
      () => {
        label.value = 'Save';
      },
      1500,
      {
        leading: false,
        trailing: true
      }
    );

    const saveBid = async () => {
      label.value = 'Saved!';
      revertLabel();
    };

    const router = useRouter();

    const createOrUpdateBid = async (e: Event, isExport: boolean) => {
      e.preventDefault();

      const updatedMaterialList = localBid.value.materialList.map(materialListItem => ({
        ...materialListItem,
        location: materialListItem.location ?? defaultLocation
      }));

      const requiredToast = () => {
        createToast(
          {
            title: 'Missing a required field!',
            description: `Please make sure all required fields are filled out.`
          },
          {
            position: 'top-center',
            type: 'danger',
            showCloseButton: true,
            timeout: 1000000,
            transition: 'slide',
            showIcon: true,
            hideProgressBar: true
          }
        );
      };

      const payload = {
        ...localBid.value,
        jobName: jobName.value,
        client: client.value,
        groupCompanyName: appDefaults.value.companyName,
        groupCompanyAddress: appDefaults.value.companyAddress,
        groupCompanyPhone: appDefaults.value.companyPhone,
        date: getTimestampNow(),
        materialList: updatedMaterialList
      };

      const checkRequiredIncluded = () => {
        if (
          payload.jobLocation == '' ||
          payload.jobName == '' ||
          payload.client.name == '' ||
          !payload.completionDate ||
          payload.materialList.length == 0
        ) {
          requiredToast();
          return false;
        }
        return true;
      };

      // TODO: reimplement bidEditTimeout after YRT approval
      // const now = getTimestampNow();
      // // If we are editing something. We know it has a createdOn property.
      // // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      // const lastUpdatedOn = localBid.value.lastUpdatedOn?.timestamp;
      // const { bidEditTimeout } = store.getters.appDefaults.get[0];

      // if (lastUpdatedOn && bidEditTimeout !== firebaseEmptyField) {
      //   const lastUpdatedOnWithTimeoutOffset = createTimestamp(
      //     lastUpdatedOn.seconds + (bidEditTimeout as number) * 3600,
      //     lastUpdatedOn.nanoseconds
      //   );

      //   if (lastUpdatedOnWithTimeoutOffset < now) {
      //     await store.dispatch.bids.update(payload);
      //     saveBid();
      //   } else {
      //     // createToast(
      //     //   {
      //     //     title: 'Failed to save bid',
      //     //     description: `You have to wait ${bidEditTimeout} hours since your last edit to this bid.`
      //     //   },
      //     //   {
      //     //     position: 'top-center',
      //     //     transition: 'slide',
      //     //     toastBackgroundColor: 'hsla(0, 100%, 50%, 0.8)'
      //     //   }
      //     // );
      //   }
      // } else {
      //   await store.dispatch.bids.update(payload);
      //   saveBid();
      // }
      const requireIncluded = checkRequiredIncluded();
      if (requireIncluded) {
        if (localBid.value.id) {
          await store.dispatch.bids.update(payload);
          saveBid();
        } else {
          await store.dispatch.bids.create(payload);
          saveBid();
        }
      }

      if (isExport) {
        if (requireIncluded) {
          exportToDrive(payload);
          // @ts-expect-error Direct Vuex wants a payload for the generic action.
          // Custom modules are not correctly typed at the moment.
          // This action does not accept a payload.
          store.dispatch.bids.clearLocalBid();
          router.push('/bids');
        }
      }
    };

    const [selectedAddEditClient, isAddEditModalOpen, openAddEditModal] = useModalState<Client>();
    const clientIsEmpty = computed(() => isEqual(client.value, emptyValues.client));
    const selectSubmittedClient = useSelectSubmittedClient(client);

    const routeToMap = () => router.push('/create/map');

    return {
      ...bidWriteableComputedStoreMethods,
      InputElementTypes,
      createOrUpdateBid,
      localBid,
      toFixed,
      client,
      selectedAddEditClient,
      isAddEditModalOpen,
      openAddEditModal,
      selectSubmittedClient,
      clientIsEmpty,
      jobName,
      routeToMap,
      localBidMaterialListLocalState,
      enumerableVolumeAndWeightUnits,
      label,
      isDeleteModalOpen,
      openDeleteModal,
      selectedDeleteMaterialListItem
    };
  }
});
